import { useState } from "react";
import { useCreateNoteMutation, useGetTicketNotesQuery } from "../../api/note";
import { Checkbox, Textarea, Spinner } from "flowbite-react";
import { Note, QueryParams } from "@/sharedTypes";
import NoteItem from "./NoteItem";
import Pagination from "@/components/Pagination";
import { createPaginationHandler } from "@/utils/paginationUtils";
import { FaPlus, FaTimes } from "react-icons/fa"; // Import icons
import UseErrorHandler from "@/components/UseErrorHandler";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";

function TicketNotes({ ticketId }: { ticketId: string }) {
  //Note data fetching section
  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: {
      pageSize: 15,
      page: 1,
    },
  });

  const {
    data: notes,
    isError,
    error,
    isLoading,
  } = useGetTicketNotesQuery({ ticketId, queryParams });

  UseErrorHandler({
    title: "Failed to load ticket notes",
    isError,
    error,
  });

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [isVisibleToClient, setIsVisibleToClient] = useState(false);
  const savedUserData = JSON.parse(localStorage.getItem("userData") || "{}");

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNote(e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsVisibleToClient(e.target.checked);
  };

  const [createNote, { isLoading: creatingNote }] = useCreateNoteMutation();

  const handleNoteSubmit = () => {
    createNote({
      note_body: newNote,
      agent_id: savedUserData.agent_id,
      ticket_id: parseInt(ticketId),
      is_visible_to_client: isVisibleToClient,
    })
      .then(() => {
        setNewNote("");
        setIsVisibleToClient(false);
        setIsAddingNote(false);
      })
      .catch((error: any) => {
        toast.error(`${getErrorMessage(error)}`);
      });
  };

  const handlePagination = createPaginationHandler(setQueryParams);

  return (
    <div className="mr-2 p-2">
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setIsAddingNote((prev) => !prev)}
          className="flex items-center rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
        >
          <span
            style={{
              display: "inline-block",
              transition: "transform 0.3s ease",
              transform: isAddingNote ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            {isAddingNote ? <FaTimes /> : <FaPlus />}
          </span>
        </button>
      </div>
      {isAddingNote && (
        <div className="mb-10 mt-4">
          <Textarea
            value={newNote}
            onChange={handleNoteChange}
            className="mb-4 w-full rounded border p-2"
            rows={4}
            placeholder="Enter your note here..."
          />
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center">
              <Checkbox
                id="visibleToClient"
                checked={isVisibleToClient}
                onChange={handleCheckboxChange}
                className="mr-2"
              />
              <label
                htmlFor="visibleToClient"
                className="text-sm text-gray-800 dark:text-gray-200"
              >
                Is visible to client
              </label>
            </div>
            <button
              onClick={handleNoteSubmit}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              disabled={creatingNote} // Disable button while loading
            >
              {creatingNote ? <Spinner size="sm" className="mr-2" /> : "Add"}
            </button>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="flex w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : notes?.items?.length > 0 ? (
        notes?.items?.map((note: Note, index: number) => (
          <NoteItem key={index} note={note} />
        ))
      ) : (
        !isAddingNote && (
          <p className="text-base text-gray-600 dark:text-gray-400">
            No notes available for this ticket.
          </p>
        )
      )}
      {notes?.items?.length > 0 && (
        <Pagination
          pagination={notes?.pagination}
          onPageChange={handlePagination}
        />
      )}
    </div>
  );
}

export default TicketNotes;
